import React, { useEffect, useMemo, useState } from "react"
import {
  Button,
  Card,
  CardBody,
  CardTitle,
  Col,
  FormGroup,
  Label,
  Row,
} from "reactstrap"
import { Formik, Form, Field } from "lib/formik"
import Breadcrumbs from "components/Common/Breadcrumb"
import BackButton from "components/Shared/BackButton"
import Spinners from "components/Shared/Spinners"
import TextError from "components/Shared/TextError"
import NativeCheckbox from "components/Shared/NativeCheckbox"
import { brandFormSchema } from "components/Common/validation"
import { useDispatch, useSelector } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import Select from "components/Shared/TranslatedSelect"
import isPermissionEnabled from "common/isPermissionEnabled"
import { showAlert } from "store/reducers/alertReducer"
import {
  addBrand,
  getBrandDetails,
  updateBrand,
} from "store/actions/brandsAction"
import {
  saAddBrand,
  saGetBrandDetails,
  saGetBrandPaymentMethods,
  saUpdateBrand,
} from "store/actions/superAdminAction"

const BRAND_FORM_SCOPE_CONFIG = {
  tenant: {
    permissionCategory: 52,
    permissions: { create: "brands_2", edit: "brands_3" },
    routes: { list: "/brands" },
    fallbackRoute: "/dashboard",
    actions: {
      addBrand,
      updateBrand,
      getBrandDetails,
    },
    viewOnly: true,
  },
  superadmin: {
    permissionCategory: 108,
    permissions: { create: "sa_brands_2", edit: "sa_brands_3" },
    routes: { list: "/superadmin/brands" },
    fallbackRoute: "/superadmin/dashboard",
    actions: {
      addBrand: saAddBrand,
      updateBrand: saUpdateBrand,
      getBrandDetails: saGetBrandDetails,
    },
    viewOnly: false,
  },
}

const initialValues = {
  brandName: "",
  appBrandId: "",
  brandDesc: "",
  integration: null,
  allowedPaymentMethods: [],
  status: 1,
}

const INTEGRATION_VALUES = ["Salasa", "Syrve", "Foodics"]

const AddBrand = ({ mode = "add", scope = "tenant" }) => {
  const config = BRAND_FORM_SCOPE_CONFIG[scope] || BRAND_FORM_SCOPE_CONFIG.tenant
  const { t } = useTranslation()
  document.title = `${mode === "add" ? t("Add") : t("Edit")} ${t("Brand")}`

  const integrationOptions = useMemo(
    () =>
      INTEGRATION_VALUES.map(value => ({
        value,
        label: t(value),
      })),
    [t]
  )

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const id = new URLSearchParams(location.search).get("id")

  const { loading, isLoading, brandDetails, paymentMethodOptions } = useSelector(
    state => state.Brands
  )
  const { userRole, accountType } = useSelector(state => state.Login)
  const [formValues, setFormValues] = useState(initialValues)

  const paymentMethodSelectOptions = useMemo(
    () =>
      (paymentMethodOptions || []).map(method => ({
        value: method,
        label: t(method, { defaultValue: method }),
      })),
    [paymentMethodOptions, t]
  )

  useEffect(() => {
    if (config.viewOnly) {
      navigate(config.routes.list)
      return
    }

    const canCreate = isPermissionEnabled(
      userRole,
      config.permissionCategory,
      config.permissions.create,
      accountType
    )
    const canEdit = isPermissionEnabled(
      userRole,
      config.permissionCategory,
      config.permissions.edit,
      accountType
    )

    if ((mode === "add" && !canCreate) || (mode === "edit" && !canEdit)) {
      navigate(config.fallbackRoute)
      return
    }

    if (mode === "edit" && id) {
      dispatch(config.actions.getBrandDetails({ id }))
    }

    if (scope === "superadmin") {
      dispatch(saGetBrandPaymentMethods())
    }
  }, [mode, id, userRole, accountType, dispatch, navigate, scope, config])

  useEffect(() => {
    if (mode === "edit" && brandDetails && !loading) {
      setFormValues({
        brandName: brandDetails.brandName || "",
        appBrandId: brandDetails.appBrandId || "",
        brandDesc: brandDetails.brandDesc || "",
        integration: brandDetails.integration || null,
        allowedPaymentMethods: Array.isArray(brandDetails.allowedPaymentMethods)
          ? brandDetails.allowedPaymentMethods
          : [],
        status: brandDetails.status === 0 ? 0 : 1,
      })
    }
  }, [brandDetails, loading, mode])

  const handleSubmit = values => {
    const { appBrandId, ...rest } = values
    const payload = {
      ...rest,
    }

    const action =
      mode === "add"
        ? dispatch(config.actions.addBrand({ ...payload, appBrandId }))
        : dispatch(config.actions.updateBrand({ id, ...payload }))

    action.then(res => {
      const { statusCode, status, msg } = res?.payload || {}
      if (statusCode === 200 && status) {
        dispatch(showAlert({ text: msg, type: "success" }))
        navigate(config.routes.list)
      } else {
        dispatch(showAlert({ text: msg ? t(msg, { defaultValue: msg }) : t("Something went wrong"), type: "error" }))
      }
    })
  }

  if (mode === "edit" && loading && !brandDetails) {
    return <Spinners />
  }

  return (
    <div className="page-content">
      <Formik
        enableReinitialize
        initialValues={formValues}
        validationSchema={brandFormSchema()}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue, setFieldTouched, touched, errors }) => (
          <Form>
            <Card>
              <CardBody>
                <CardTitle className="d-flex align-items-center gap-2">
                  <BackButton />
                  {mode === "add" ? t("Add Brand") : t("Edit Brand")}
                </CardTitle>

                <Row className="mt-3">
                  <Col md={6}>
                    <FormGroup>
                      <Label>
                        {t("Brand Name")} <span className="text-danger">*</span>
                      </Label>
                      <Field
                        name="brandName"
                        className="form-control"
                        disabled={mode === "edit"}
                      />
                      <TextError name="brandName" />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label>
                        {t("App Brand ID")} <span className="text-danger">*</span>
                      </Label>
                      <Field
                        name="appBrandId"
                        className="form-control"
                        disabled={mode === "edit"}
                      />
                      <TextError name="appBrandId" />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label>{t("Integration")}</Label>
                      <Select
                        value={
                          integrationOptions.find(
                            option => option.value === values.integration
                          ) || null
                        }
                        onChange={option => {
                          setFieldValue("integration", option?.value ?? null, true)
                        }}
                        onBlur={() => setFieldTouched("integration", true, false)}
                        options={integrationOptions}
                        isClearable
                        placeholder={t("Select Integration (optional)")}
                        classNamePrefix="select2-selection"
                      />
                      <TextError name="integration" />
                    </FormGroup>
                  </Col>
                  <Col md={12}>
                    <FormGroup>
                      <Label>{t("Description")}</Label>
                      <Field
                        as="textarea"
                        rows={3}
                        name="brandDesc"
                        className="form-control"
                      />
                      <TextError name="brandDesc" />
                    </FormGroup>
                  </Col>

                  {scope === "superadmin" ? (
                    <Col md={12}>
                      <FormGroup>
                        <Label>{t("Allowed Payment Methods")}</Label>
                        <Select
                          isMulti
                          value={paymentMethodSelectOptions.filter(option =>
                            (values.allowedPaymentMethods || []).includes(option.value)
                          )}
                          onChange={options => {
                            setFieldValue(
                              "allowedPaymentMethods",
                              (options || []).map(option => option.value),
                              true
                            )
                          }}
                          onBlur={() =>
                            setFieldTouched("allowedPaymentMethods", true, false)
                          }
                          options={paymentMethodSelectOptions}
                          placeholder={t("Select payment methods")}
                          classNamePrefix="select2-selection"
                          styles={{
                            multiValue: provided => ({
                              ...provided,
                              backgroundColor: "#556ee6",
                            }),
                            multiValueLabel: provided => ({
                              ...provided,
                              color: "#fff",
                            }),
                            multiValueRemove: provided => ({
                              ...provided,
                              color: "#fff",
                              ":hover": {
                                backgroundColor: "#4458b8",
                                color: "#fff",
                              },
                            }),
                          }}
                        />
                        <TextError name="allowedPaymentMethods" />
                      </FormGroup>
                    </Col>
                  ) : null}

                  <Col md={12}>
                    <NativeCheckbox
                      id="brand-active"
                      checked={values.status === 1}
                      className="d-inline-flex align-items-center gap-2"
                      onActivate={() =>
                        setFieldValue("status", values.status === 1 ? 0 : 1)
                      }
                    >
                      <span>{t("Active")}</span>
                    </NativeCheckbox>
                  </Col>
                </Row>

                <div className="d-flex justify-content-end gap-2 mt-3">
                  <Button color="light" type="button" onClick={() => navigate("/brands")}>
                    {t("Cancel")}
                  </Button>
                  <Button color="primary" type="submit" disabled={isLoading}>
                    {isLoading
                      ? t("Saving...")
                      : mode === "add"
                      ? t("Create Brand")
                      : t("Update Brand")}
                  </Button>
                </div>
              </CardBody>
            </Card>
          </Form>
        )}
      </Formik>
    </div>
  )
}

export default AddBrand
